import React from 'react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { AnalyticsFilters as FilterState, UploadedDataset, getCategoryValues } from '../../services/analytics';

interface AnalyticsFiltersProps {
  dataset: UploadedDataset;
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({ dataset, filters, onChange }) => {
  const metricProfiles = dataset.profiles.filter((profile) => profile.role === 'metric');
  const dateProfiles = dataset.profiles.filter((profile) => profile.role === 'date');
  const categoryProfiles = dataset.profiles.filter((profile) => profile.role === 'category');
  const categoryValues = getCategoryValues(dataset, filters.categoryColumn);

  const update = (nextFilters: Partial<FilterState>) => {
    onChange({ ...filters, ...nextFilters });
  };

  return (
    <div className="premium-card" style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <span className="icon-tile" style={{ width: '34px', height: '34px' }}>
            <SlidersHorizontal size={17} />
          </span>
          <strong>Filters</strong>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange({})}>
          <RotateCcw size={15} />
          Reset
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))', gap: '1rem', alignItems: 'end' }}>
        <Select
          label="Metric"
          value={filters.metricColumn ?? ''}
          onChange={(event) => update({ metricColumn: event.target.value || undefined })}
        >
          <option value="">Auto detected</option>
          {metricProfiles.map((profile) => (
            <option key={profile.name} value={profile.name}>{profile.name}</option>
          ))}
        </Select>

        <Select
          label="Date Column"
          value={filters.dateColumn ?? ''}
          onChange={(event) => update({ dateColumn: event.target.value || undefined })}
        >
          <option value="">Auto detected</option>
          {dateProfiles.map((profile) => (
            <option key={profile.name} value={profile.name}>{profile.name}</option>
          ))}
        </Select>

        <Select
          label="Category"
          value={filters.categoryColumn ?? ''}
          onChange={(event) => update({ categoryColumn: event.target.value || undefined, categoryValue: undefined })}
        >
          <option value="">Auto detected</option>
          {categoryProfiles.map((profile) => (
            <option key={profile.name} value={profile.name}>{profile.name}</option>
          ))}
        </Select>

        <Select
          label="Filter Value"
          value={filters.categoryValue ?? ''}
          onChange={(event) => update({ categoryValue: event.target.value || undefined })}
          disabled={!filters.categoryColumn}
        >
          <option value="">All values</option>
          {categoryValues.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </Select>

        <Input
          label="Start Date"
          type="date"
          value={filters.startDate ?? ''}
          onChange={(event) => update({ startDate: event.target.value || undefined })}
        />

        <Input
          label="End Date"
          type="date"
          value={filters.endDate ?? ''}
          onChange={(event) => update({ endDate: event.target.value || undefined })}
        />

        <Input
          label="Search Rows"
          type="search"
          placeholder="Find any row"
          value={filters.search ?? ''}
          onChange={(event) => update({ search: event.target.value })}
        />
      </div>
    </div>
  );
};

export default AnalyticsFilters;
